import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const Wrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #000;
  margin: 0 0 40px 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    margin-bottom: 16px;
  }
`;

const ContentBox = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 40px;
  line-height: 1.8;

  @media (max-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 6px;
  }

  * {
    margin-bottom: 14px;

    @media (max-width: 480px) {
      margin-bottom: 10px;
    }
  }

  h2,
  h3 {
    margin-top: 24px;
    margin-bottom: 16px;
    color: #000;
    font-weight: 700;
    line-height: 1.3;

    @media (max-width: 768px) {
      margin-top: 16px;
      margin-bottom: 12px;
    }

    @media (max-width: 480px) {
      font-size: 16px;
      margin-top: 12px;
      margin-bottom: 10px;
    }
  }

  h2 {
    font-size: 24px;

    @media (max-width: 480px) {
      font-size: 18px;
    }
  }

  h3 {
    font-size: 18px;

    @media (max-width: 480px) {
      font-size: 15px;
    }
  }

  p {
    color: #333;
    font-size: 14px;

    @media (max-width: 480px) {
      font-size: 13px;
      line-height: 1.6;
    }
  }

  ul,
  ol {
    margin-left: 28px;
    padding: 0;

    @media (max-width: 768px) {
      margin-left: 20px;
    }

    @media (max-width: 480px) {
      margin-left: 16px;
    }
  }

  li {
    margin-bottom: 8px;
    color: #333;
    font-size: 14px;

    @media (max-width: 480px) {
      margin-bottom: 6px;
      font-size: 13px;
      line-height: 1.5;
    }
  }

  a {
    color: #2166a1;
    text-decoration: underline;
    word-break: break-word;

    &:active {
      color: #1a4d7f;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 13px;
    overflow-x: auto;
    display: block;
    margin-left: -40px;
    margin-right: -40px;
    padding: 0 40px;

    @media (max-width: 768px) {
      margin-left: -24px;
      margin-right: -24px;
      padding: 0 24px;
      font-size: 12px;
    }

    @media (max-width: 480px) {
      margin-left: -16px;
      margin-right: -16px;
      padding: 0 16px;
      font-size: 11px;
      overflow-x: auto;
    }
  }

  td,
  th {
    border: 1px solid #ddd;
    padding: 12px;
    text-align: left;
    word-break: break-word;

    @media (max-width: 480px) {
      padding: 8px;
      font-size: 11px;
    }
  }

  th {
    background: #f0f0f0;
    font-weight: 600;
  }
`;

const SkeletonLine = styled.div`
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  margin-bottom: 12px;
  border-radius: 4px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  &.title {
    height: 32px;
    margin-bottom: 30px;
  }

  &.heading {
    height: 24px;
    margin-top: 20px;
    margin-bottom: 16px;
  }
`;

const SkeletonLoader = () => (
  <ContentBox>
    <SkeletonLine className="title" />
    {[...Array(6)].map((_, i) => (
      <div key={i}>
        <SkeletonLine className="heading" />
        <SkeletonLine />
        <SkeletonLine style={{ width: "85%" }} />
        <SkeletonLine style={{ width: "90%" }} />
      </div>
    ))}
  </ContentBox>
);

const TermsAndConditions = () => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/policies`,
      );
      const data = await response.json();

      if (data.status && data.data) {
        setContent(data.data.terms_and_conditions);
      } else {
        setError("Failed to load terms and conditions");
      }
    } catch (err) {
      console.error("Error fetching policies:", err);
      setError("Error loading terms and conditions. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {/* <Navbar /> */}
      <Wrapper>
        <PageTitle>Terms & Conditions</PageTitle>
        {loading ? (
          <SkeletonLoader />
        ) : error ? (
          <ContentBox style={{ color: "#d32f2f", textAlign: "center" }}>
            {error}
          </ContentBox>
        ) : (
          <ContentBox
            dangerouslySetInnerHTML={{
              __html: content,
            }}
          />
        )}
      </Wrapper>
      <Footer />
    </Container>
  );
};

export default TermsAndConditions;
